/**
 * engine/unlock.ts — the first `play()`, and every one after it.
 *
 * Task 07, carried over from the old project. There is exactly one moment in the life of the
 * app that cannot be retried: the first `play()`. The browser counts it as the listener's own
 * only while it runs inside the tap handler — a `play()` after `await fetch(…)` is a different
 * task, the element stays locked, and every later `play()` fails with `NotAllowedError`,
 * including the ones the player makes by itself.
 *
 * So {@link unlockWith} is **not** an `async` function, and does its work in a strict order
 * with no `await` before `play()`:
 *
 * ```
 * el.src = src   →   el.currentTime = start   →   el.play()
 * ```
 *
 * Writing `currentTime` before anything is loaded is not a seek: the media spec turns it into
 * the element's default start position, applied once the file is open. That is how a track
 * resumes mid-way without a jump from zero and without a listener that would have to run
 * outside the tap.
 *
 * `spec.md` «Плеер»: «нет попытки автозапуска до жеста» — nothing in the app calls `play()`
 * before the listener has tapped something; this module has no code that runs at load.
 */

import type { MediaElementLike } from './element';

/**
 * Why a `play()` did not start.
 *
 * - `gesture` — `NotAllowedError`: the element is locked. Show a «play» button; only a tap
 *   can clear it.
 * - `unsupported` — `NotSupportedError`: the element latched an error, and only `load()`
 *   clears it.
 * - `aborted` — a pause or a new `src` raced the pending `play()`. That is what a fast skip
 *   looks like; not an error.
 * - `unknown` — anything else.
 */
export type PlayFailure = 'gesture' | 'unsupported' | 'aborted' | 'unknown';

export type PlayOutcome =
  { readonly ok: true } | { readonly ok: false; readonly reason: PlayFailure };

function errorName(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'name' in error) {
    const name: unknown = error.name;
    if (typeof name === 'string') return name;
  }
  return '';
}

/** A rejection of `play()` as a {@link PlayFailure}. Some webviews reject with non-`Error`s. */
export function classifyPlayRejection(error: unknown): PlayFailure {
  switch (errorName(error)) {
    case 'NotAllowedError':
      return 'gesture';
    case 'NotSupportedError':
      return 'unsupported';
    case 'AbortError':
      return 'aborted';
    default:
      return 'unknown';
  }
}

/** `play()`, with a synchronous throw turned into the same outcome as a rejection. */
function attemptPlay(el: MediaElementLike): Promise<PlayOutcome> {
  try {
    return el.play().then(
      (): PlayOutcome => ({ ok: true }),
      (error: unknown): PlayOutcome => ({ ok: false, reason: classifyPlayRejection(error) }),
    );
  } catch (error) {
    return Promise.resolve({ ok: false, reason: classifyPlayRejection(error) });
  }
}

/**
 * The elements a `play()` has succeeded on. Per element, because that is where the browser
 * keeps the fact (`./element.ts`): a flag for the module would claim a new element unlocked.
 */
const unlockedElements = new WeakSet<MediaElementLike>();

/** Records the unlock and, the first time, raises `preload` — allowed on this element now. */
function markUnlocked(el: MediaElementLike): void {
  if (unlockedElements.has(el)) return;
  unlockedElements.add(el);
  // Before the unlock it would have been a speculative download on a metered connection.
  el.preload = 'metadata';
}

/**
 * Points the element at `src`, at `startS` seconds, and plays — all synchronously, so it may
 * (and must) be called straight from a tap handler. See the header.
 */
export function unlockWith(el: MediaElementLike, src: string, startS = 0): Promise<PlayOutcome> {
  /* ── ORDER IS LOAD-BEARING. No `await` above `play()`. ─────────────────────── */
  el.src = src;
  if (startS > 0 && Number.isFinite(startS)) el.currentTime = startS;
  const started = attemptPlay(el);
  /* ── below here may be asynchronous ──────────────────────────────────────── */
  return started.then((outcome) => {
    if (outcome.ok) markUnlocked(el);
    return outcome;
  });
}

/**
 * `load()`, then `play()` — the answer to `NotSupportedError` (`spec.md` «Плеер»: «перезагружает
 * элемент перед повтором»). The element latched the error, and only the load algorithm clears
 * it, so a bare second `play()` would fail the same way.
 */
export async function reloadAndPlay(el: MediaElementLike): Promise<PlayOutcome> {
  try {
    el.load();
  } catch {
    return { ok: false, reason: 'unsupported' };
  }
  const outcome = await attemptPlay(el);
  if (outcome.ok) markUnlocked(el);
  return outcome;
}

/**
 * `play()` for everything that is not a fresh `src`: resuming, a lock-screen button, a retry.
 *
 * `NotSupportedError` gets `load()` and one more try — the latched error cannot be cleared any
 * other way. `NotAllowedError` is never retried: only a tap can clear it. Never throws.
 */
export async function safePlay(el: MediaElementLike): Promise<PlayOutcome> {
  const first = await attemptPlay(el);
  if (first.ok) {
    markUnlocked(el);
    return first;
  }
  return first.reason === 'unsupported' ? await reloadAndPlay(el) : first;
}
